/**
 * Organization Detail Page
 * 
 * Full organization view with teams, members, and settings.
 * Users can create teams, manage members, and access all org functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faUsers,
  faUserPlus,
  faPlus,
  faArrowLeft,
  faBox,
  faShield,
  faEdit,
  faTrash,
  faCrown,
  faUserShield,
  faUserEdit,
  faEye,
  faUserTag,
  faPalette,
  faCheck,
  faTimes,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { workspacesAPI, teamsAPI } from '../services/api';
import { useRBAC } from '../hooks/useRBAC';
import { 
  WorkspaceRole, 
  WORKSPACE_ROLE_DISPLAY_NAMES,
  Permission,
} from '../types/rbac';
import '../styles/OrganizationDetail.css';

interface Team {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  _count?: {
    members: number;
  };
}

interface Member {
  id: string;
  role: WorkspaceRole;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  type: 'ORGANIZATION';
  createdAt: string;
  members?: Member[];
  _count?: {
    items: number;
    categories: number;
  };
}

const TEAM_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

const TEAM_ICONS = [
  'users', 'code', 'chart-line', 'cog', 'star',
  'rocket', 'globe', 'shield', 'bolt', 'flag',
];

const OrganizationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, workspaceRole } = useRBAC();
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modals
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  
  // Create team form
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [newTeamColor, setNewTeamColor] = useState(TEAM_COLORS[0]);
  const [newTeamIcon, setNewTeamIcon] = useState(TEAM_ICONS[0]);
  
  // Invite member form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('VIEWER');
  
  // Edit member form
  const [editMemberRole, setEditMemberRole] = useState<WorkspaceRole>('VIEWER');
  
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workspacesAPI.getById(id!);
      setOrganization(response.data);
      setMembers(response.data.members || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await teamsAPI.getAll(id!);
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchOrganization();
      fetchTeams();
    }
  }, [id, fetchOrganization, fetchTeams]);

  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    
    try {
      setActionLoading(true);
      await teamsAPI.create({
        name: newTeamName.trim(),
        description: newTeamDescription.trim() || undefined,
        workspaceId: id!,
        icon: newTeamIcon,
        color: newTeamColor,
      });
      
      setShowCreateTeamModal(false);
      setNewTeamName('');
      setNewTeamDescription('');
      setNewTeamColor(TEAM_COLORS[0]);
      setNewTeamIcon(TEAM_ICONS[0]);
      setActionSuccess('Team created successfully!');
      fetchTeams();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;
    
    try {
      setActionLoading(true);
      await workspacesAPI.inviteMember(id!, inviteEmail.trim(), inviteRole);
      
      setShowInviteMemberModal(false);
      setInviteEmail('');
      setInviteRole('VIEWER');
      setActionSuccess('Invitation sent successfully!');
      fetchOrganization();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to invite member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateMemberRole = async () => {
    if (!selectedMember) return;
    
    try {
      setActionLoading(true);
      await workspacesAPI.updateMemberRole(id!, selectedMember.user.id, editMemberRole);
      
      setShowEditMemberModal(false);
      setSelectedMember(null);
      setActionSuccess('Member role updated!');
      fetchOrganization();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update member role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await workspacesAPI.removeMember(id!, userId);
      setActionSuccess('Member removed!');
      fetchOrganization();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const openEditMemberModal = (member: Member) => {
    setSelectedMember(member);
    setEditMemberRole(member.role);
    setShowEditMemberModal(true);
  };

  const getRoleIcon = (role: WorkspaceRole) => {
    switch (role) {
      case 'OWNER': return faCrown;
      case 'ORG_ADMIN': return faUserShield;
      case 'MANAGER': return faUserEdit;
      case 'REVIEWER': return faEye;
      case 'VIEWER': return faEye;
      case 'GUEST': return faUserTag;
      default: return faUsers;
    }
  };

  const canManageMembers = hasPermission(Permission.MEMBER_INVITE) || workspaceRole === 'OWNER' || workspaceRole === 'ORG_ADMIN';
  const canCreateTeams = hasPermission(Permission.TEAM_CREATE) || workspaceRole === 'OWNER' || workspaceRole === 'ORG_ADMIN' || workspaceRole === 'MANAGER';

  if (loading) {
    return (
      <div className="organization-detail">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="organization-detail">
        <div className="error-state">
          <FontAwesomeIcon icon={faTimes} />
          <p>{error}</p>
          <button onClick={() => navigate('/workspaces')}>Back to Workspaces</button>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-detail">
      {/* Header */}
      <div className="org-header">
        <button className="back-btn" onClick={() => navigate('/workspaces')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        
        <div className="org-info">
          <div className="org-icon">
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <div className="org-meta">
            <h1>{organization?.name}</h1>
            {organization?.description && (
              <p className="org-description">{organization.description}</p>
            )}
            <div className="org-stats">
              <span><FontAwesomeIcon icon={faUsers} /> {members.length} members</span>
              <span><FontAwesomeIcon icon={faUsers} /> {teams.length} teams</span>
              <span><FontAwesomeIcon icon={faBox} /> {organization?._count?.items || 0} items</span>
            </div>
          </div>
        </div>
        
        <div className="org-actions">
          {canManageMembers && (
            <button className="btn-secondary" onClick={() => setShowInviteMemberModal(true)}>
              <FontAwesomeIcon icon={faUserPlus} /> Invite Member
            </button>
          )}
          {canCreateTeams && (
            <button className="btn-primary" onClick={() => setShowCreateTeamModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> Create Team
            </button>
          )}
          {(workspaceRole === 'OWNER' || workspaceRole === 'ORG_ADMIN') && (
            <button className="btn-secondary" onClick={() => navigate(`/organizations/${id}/settings`)}>
              <FontAwesomeIcon icon={faShield} /> Settings
            </button>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      
      {actionSuccess && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{actionSuccess}</span>
        </div>
      )}

      <div className="org-content">
        {/* Teams Section */}
        <section className="org-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faUsers} /> Teams</h2>
          </div>
          
          {teams.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faUsers} />
              <h3>No teams yet</h3>
              <p>Create teams to organize your members and projects</p>
              {canCreateTeams && (
                <button className="btn-primary" onClick={() => setShowCreateTeamModal(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Create First Team
                </button>
              )}
            </div>
          ) : (
            <div className="teams-grid">
              {teams.map((team) => (
                <Link
                  key={team.id}
                  to={`/teams/${team.id}`}
                  className="team-card"
                  style={{ borderLeftColor: team.color || TEAM_COLORS[0] }}
                >
                  <div className="team-icon" style={{ backgroundColor: team.color || TEAM_COLORS[0] }}>
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div className="team-info">
                    <h3>{team.name}</h3>
                    {team.description && <p>{team.description}</p>}
                    <span className="team-members">
                      <FontAwesomeIcon icon={faUsers} /> {team._count?.members || 0} members
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Members Section */}
        <section className="org-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faUsers} /> Members</h2>
          </div>
          
          <div className="members-list">
            {members.map((member) => (
              <div key={member.id} className="member-row">
                <div className="member-avatar">
                  {member.user.name?.charAt(0).toUpperCase() || member.user.email.charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <div className="member-name">
                    {member.user.name || member.user.email}
                    {member.role === 'OWNER' && (
                      <span className="owner-badge">
                        <FontAwesomeIcon icon={faCrown} /> Owner
                      </span>
                    )}
                  </div>
                  <div className="member-email">{member.user.email}</div>
                </div>
                <div className="member-role">
                  <FontAwesomeIcon icon={getRoleIcon(member.role)} />
                  {WORKSPACE_ROLE_DISPLAY_NAMES[member.role]}
                </div>
                {canManageMembers && member.role !== 'OWNER' && (
                  <div className="member-actions">
                    <button
                      className="btn-icon"
                      onClick={() => openEditMemberModal(member)}
                      title="Edit role"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleRemoveMember(member.user.id)}
                      title="Remove member"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Create Team Modal */}
      {showCreateTeamModal && (
        <div className="modal-overlay" onClick={() => setShowCreateTeamModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faPlus} /> Create Team</h2>
              <button className="close-btn" onClick={() => setShowCreateTeamModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  placeholder="Describe this team's purpose"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label><FontAwesomeIcon icon={faPalette} /> Team Color</label>
                <div className="color-picker">
                  {TEAM_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${newTeamColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewTeamColor(color)}
                    >
                      {newTeamColor === color && <FontAwesomeIcon icon={faCheck} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateTeamModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateTeam}
                disabled={!newTeamName.trim() || actionLoading}
              >
                {actionLoading ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteMemberModal && (
        <div className="modal-overlay" onClick={() => setShowInviteMemberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faUserPlus} /> Invite Member</h2>
              <button className="close-btn" onClick={() => setShowInviteMemberModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label><FontAwesomeIcon icon={faEnvelope} /> Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="member@example.com"
                />
              </div>
              <div className="form-group">
                <label><FontAwesomeIcon icon={faShield} /> Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as WorkspaceRole)}
                >
                  <option value="ORG_ADMIN">Organization Admin</option>
                  <option value="MANAGER">Manager</option>
                  <option value="REVIEWER">Reviewer</option>
                  <option value="VIEWER">Viewer</option>
                  <option value="GUEST">Guest</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowInviteMemberModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleInviteMember}
                disabled={!inviteEmail.trim() || actionLoading}
              >
                {actionLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Role Modal */}
      {showEditMemberModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowEditMemberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faUserEdit} /> Edit Member Role</h2>
              <button className="close-btn" onClick={() => setShowEditMemberModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="member-preview">
                <div className="member-avatar">
                  {selectedMember.user.name?.charAt(0).toUpperCase() || 
                   selectedMember.user.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="member-name">{selectedMember.user.name || selectedMember.user.email}</div>
                  <div className="member-email">{selectedMember.user.email}</div>
                </div>
              </div>
              <div className="form-group">
                <label><FontAwesomeIcon icon={faShield} /> Role</label>
                <select
                  value={editMemberRole}
                  onChange={(e) => setEditMemberRole(e.target.value as WorkspaceRole)}
                >
                  <option value="ORG_ADMIN">Organization Admin</option>
                  <option value="MANAGER">Manager</option>
                  <option value="REVIEWER">Reviewer</option>
                  <option value="VIEWER">Viewer</option>
                  <option value="GUEST">Guest</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditMemberModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpdateMemberRole}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationDetail;
